import type { KollaProvidersEntityOrResourcesEntity } from 'app/services/kolla'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { ColumnDef } from '@tanstack/react-table'
import { ReactNode, useState } from 'react'
import { Tooltip } from 'app/components/Tooltip'
import type { AppointmentTypeJson } from 'app/types/entities'
import type { AvailabilityType } from 'app/types/entities'
import { OPEN_HOURS_DAYS } from 'app/components/scheduling/availabilityHours.utils'
import { convertFrom24To12Format } from 'lib/convert-24-to-12-format'

/** Formats operatory label for table display, e.g., "OP-6 - Dr. Ben (FORD)" */
function formatOperatoryLabel(op: KollaProvidersEntityOrResourcesEntity): string {
  const shortcode = op.additional_data?.short_display_name
  const base = `${op.remote_id} - ${op.display_name}`
  return shortcode ? `${base} (${shortcode})` : base
}

export const availabilityColumns = (
  appointmentTypes?: AppointmentTypeJson[],
  operatories?: KollaProvidersEntityOrResourcesEntity[],
  onEditAvailabilityClick?: (availability: AvailabilityType) => void,
  isV2?: boolean,
): ColumnDef<AvailabilityType, any>[] => [
  {
    header: `Types`,
    id: `Types`,
    meta: {
      ControlledComponent: (row, column) => {
        const [tooltipContent, setTooltipContent] = useState<ReactNode>(undefined)
        return (
          <div className="flex items-center justify-between px-2">
            <Tooltip
              className="cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap"
              placement="bottom"
              message={tooltipContent}
              onMouseOver={(event) => {
                if (
                  event.currentTarget instanceof HTMLElement &&
                  event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                ) {
                  setTooltipContent(
                    row!.appointmentTypeIds?.map((appointmentTypeId) => {
                      const appointmentType = appointmentTypes?.find((at) => at.vendorId === appointmentTypeId)
                      return <p key={appointmentType?.vendorId}>{appointmentType?.displayName}</p>
                    }),
                  )
                } else {
                  setTooltipContent(undefined)
                }
              }}
            >
              <div className="flex flex-col gap-1">
                {row!.appointmentTypeIds?.map((appointmentTypeId) => {
                  const appointmentType = appointmentTypes?.find((at) => at.vendorId === appointmentTypeId)
                  return <p key={appointmentType?.vendorId}>{appointmentType?.displayName}</p>
                })}
              </div>
            </Tooltip>

            <button
              onClick={(e) => {
                onEditAvailabilityClick?.(row!)
              }}
              className="mr-2 hidden h-8 items-center rounded-lg border border-strong bg-surface-subtle p-2 text-ui-xs text-strong group-hover:flex"
            >
              <p>Edit</p>
              <ChevronRightIcon height={16} width={16} />
            </button>
          </div>
        )
      },
      defaultVisible: true,
      pinned: false,
    },
    size: 280,
    enableResizing: true,
    enableHiding: false,
    enableSorting: false,
  },
  {
    header: `Operatory`,
    id: `Operatory`,
    accessorFn: (row) => row.operatoryId,
    cell: (props) => {
      if (isV2) {
        const ids = props.row.original.operatoryIds?.length
          ? props.row.original.operatoryIds
          : props.row.original.operatoryId
            ? [props.row.original.operatoryId]
            : []
        const resolved = ids
          .map((id) => operatories?.find((o) => o.remote_id === id))
          .filter(Boolean) as KollaProvidersEntityOrResourcesEntity[]
        const visible = resolved.slice(0, 2)
        const overflow = resolved.length - 2
        return (
          <div className="flex flex-wrap gap-1 px-2">
            {visible.map((op) => (
              <span key={op.remote_id} className="rounded bg-surface-muted px-2 py-0.5 text-ui-xs text-default">
                {formatOperatoryLabel(op)}
              </span>
            ))}
            {overflow > 0 && (
              <span className="rounded bg-surface-muted px-2 py-0.5 text-ui-xs text-default">+{overflow}</span>
            )}
          </div>
        )
      }
      const operatory = operatories?.find((o) => o.remote_id === props.row.original.operatoryId)
      if (!operatory) return <div></div>
      return (
        <div className="px-2">
          <span className="rounded bg-surface-muted px-2 py-0.5 text-ui-xs text-default">
            {formatOperatoryLabel(operatory)}
          </span>
        </div>
      )
    },
    meta: {
      defaultVisible: true,
      pinned: false,
    },
    size: isV2 ? 200 : 180,
    enableResizing: true,
    enableHiding: false,
    enableSorting: false,
  },
  {
    header: `Availability`,
    id: `Availability`,
    accessorFn: (row) => row.timeSlots,
    meta: {
      ControlledComponent: (row, column, index) => (
        <div className="flex h-full">
          {/* A fixed day list, not the object's keys: the GraphQL response also carries `__typename`. */}
          {/* min-w-0 gives every day an equal share of the width, and grow fills the row height, so the days line up in every row. */}
          {OPEN_HOURS_DAYS.map((day) => {
            const hours = row!.timeSlots?.[day]
            return (
              <div key={day} className="flex min-w-0 grow basis-0 flex-col border-r border-strong">
                {index === 0 && (
                  <div className="shrink-0 truncate border-b border-strong bg-surface-strong py-1 pl-2 text-ui-xs capitalize text-subtle">
                    {day}
                  </div>
                )}
                {/* Saved slots can hold a null day or null hours; both render as a closed day. */}
                {hours?.open ? (
                  <div className="min-h-[60px] grow whitespace-normal p-2">
                    {(hours.hours ?? []).map((slot, i) => (
                      <p key={i}>
                        {convertFrom24To12Format(slot.from)} - {convertFrom24To12Format(slot.to)}
                      </p>
                    ))}
                  </div>
                ) : (
                  <div className="empty-availability-cell min-h-[60px] grow"></div>
                )}
              </div>
            )
          })}
        </div>
      ),
      defaultVisible: true,
      pinned: false,
    },
    enableResizing: true,
    enableHiding: false,
    enableSorting: false,
  },
  // {
  //   id: `menu`,
  //   header: ``,
  //   size: 60,
  //   enableResizing: false,
  //   meta: {
  //     ControlledComponent: (row, column) => {
  //       return (
  //         <button
  //           className="flex h-full w-[60px] items-center justify-center"
  //           onClick={() => onEditAvailabilityClick(row!)}
  //         >
  //           <EditPencil className="h-4 w-4 text-trn-900" />
  //         </button>
  //       )
  //     },
  //     defaultVisible: true,
  //     pinned: false,
  //   },
  //   enableSorting: false,
  //   enableHiding: false,
  // },
]
